import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Divider,
  Drawer,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  ModalClose,
  Option,
  Select,
  Sheet,
  Stack,
  ToggleButtonGroup,
  Typography,
} from '@mui/joy';
import {
  ArrowSquareOut as OpenIcon,
  ArrowCounterClockwise as UndoIcon,
  Polygon as TraceIcon,
  Prohibit as SkipIcon,
  Ruler as MeasureIcon,
} from '@phosphor-icons/react';
import { GoogleStreetView } from '../measurements/GoogleStreetView';
import { usePolygonDrawing } from '../measurements/usePolygonDrawing';
import {
  formatMoney,
  formatQuoteRange,
  mailerPinsApi,
  moneyToNumber,
  pinLatLng,
} from '../../lib/directMailApi';
import { getApiErrorMessage } from '../../lib/apiError';

const SQFT_PER_SQUARE_METRE = 10.7639;
const TRACE_FILL_COLOR = '#8b5cf6';
import type { CompleteMeasurementRequest, MailerPin } from '../../types/directMail';
import {
  PITCH_BUCKET_LABELS,
  PITCH_OPTIONS,
  ROOF_QUOTE_SETUP_PATH,
  bucketForPitch,
  previewQuote,
  resolveTier,
  type RoofQuoteTier,
  type RoofQuoteWidgetConfig,
} from './roofQuotePricing';

const FULL_MEASUREMENT_PATH = '/operations/measurements';
const SQFT_PER_SQUARE = 100;

type AreaUnit = 'squares' | 'sqft';

function numberOrNull(value: string): number | null {
  if (!value.trim()) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/** 0.15 → "15%", 0.155 → "15.5%" — the way a waste factor is written on a proposal. */
function formatWaste(factor: number): string {
  return `${Number((factor * 100).toFixed(1))}%`;
}

export interface MeasurePinDrawerProps {
  open: boolean;
  pin: MailerPin | null;
  widget?: RoofQuoteWidgetConfig;
  campaignTierId?: number | null;
  /** True while the next pin in the queue exists, so "Save & next" can be offered. */
  hasNext: boolean;
  onClose: () => void;
  onSaved: (pin: MailerPin, advance: boolean) => void;
  onSkip: (pin: MailerPin) => void;
}

export const MeasurePinDrawer: React.FC<MeasurePinDrawerProps> = ({
  open,
  pin,
  widget,
  campaignTierId,
  hasNext,
  onClose,
  onSaved,
  onSkip,
}) => {
  const [unit, setUnit] = useState<AreaUnit>('squares');
  const [area, setArea] = useState('');
  const [pitch, setPitch] = useState('6/12');
  const [wastePercent, setWastePercent] = useState('');
  const [tierId, setTierId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<MailerPin | null>(null);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const [mapReady, setMapReady] = useState(false);

  const tiers = widget?.tiers ?? [];
  const defaultTier = useMemo(() => resolveTier(tiers, campaignTierId), [tiers, campaignTierId]);
  const activeTier: RoofQuoteTier | null = useMemo(
    () => tierId === null ? defaultTier : resolveTier(tiers, tierId),
    [tiers, tierId, defaultTier],
  );

  useEffect(() => {
    if (!pin) return;
    setError(null);
    setSaved(null);
    setTierId(null);
    const sqft = moneyToNumber(pin.roof_area_sqft);
    if (sqft > 0) {
      setUnit('squares');
      setArea(String(Number((sqft / SQFT_PER_SQUARE).toFixed(4))));
    } else {
      setArea('');
    }
    setPitch(pin.predominant_pitch || ({ low: '2/12', med: '6/12', steep: '12/12' }[pin.pitch_bucket || 'med']));
    const pinWaste = moneyToNumber(pin.waste_factor);
    setWastePercent(pin.waste_factor != null ? String(Math.round(pinWaste * 1000) / 10) : '');
  }, [pin]);

  // The drawer unmounts its children when closed, so the container only exists
  // after the open animation starts — poll for it the way the map pages do.
  useEffect(() => {
    if (!open) {
      mapRef.current = null;
      markerRef.current = null;
      setMapReady(false);
      return;
    }

    let cancelled = false;
    let attempts = 0;
    let timer = 0;

    const attach = () => {
      if (cancelled) return;
      if (window.google?.maps && containerRef.current) {
        mapRef.current = new google.maps.Map(containerRef.current, {
          center: { lat: 0, lng: 0 },
          zoom: 20,
          mapTypeId: google.maps.MapTypeId.SATELLITE,
          tilt: 0,
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: 'greedy',
        });
        setMapReady(true);
        return;
      }
      attempts += 1;
      if (attempts < 50) timer = window.setTimeout(attach, 100);
    };

    timer = window.setTimeout(attach, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map || !pin) return;

    const position = pinLatLng(pin);
    markerRef.current?.setMap(null);
    markerRef.current = new google.maps.Marker({
      position,
      map,
      icon: {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
          '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><circle cx="12" cy="12" r="7" fill="#0084ff" stroke="white" stroke-width="3"/></svg>',
        )}`,
        scaledSize: new google.maps.Size(24, 24),
        anchor: new google.maps.Point(12, 12),
      },
    });
    map.setCenter(position);
    map.setZoom(20);

    // The drawer slides in, so the map sizes itself against a container that is
    // still moving; a resize once it has settled is what stops the grey tiles.
    const settle = window.setTimeout(() => {
      google.maps.event.trigger(map, 'resize');
      map.setCenter(position);
    }, 300);
    return () => window.clearTimeout(settle);
  }, [mapReady, pin]);

  // Traced roof sections. A house plus a detached garage is two outlines, so
  // the field takes their sum rather than the last one drawn.
  const [tracing, setTracing] = useState(false);
  const [tracedPaths, setTracedPaths] = useState<Array<Array<{ lat: number; lng: number }>>>([]);
  const tracedShapesRef = useRef<google.maps.Polygon[]>([]);

  const clearTraces = useCallback(() => {
    tracedShapesRef.current.forEach((shape) => shape.setMap(null));
    tracedShapesRef.current = [];
    setTracedPaths([]);
  }, []);

  // Vertices already on the map, so an adjoining section snaps to the wall it
  // shares instead of leaving a sliver between the two.
  const snapTargets = useMemo(() => tracedPaths.flat(), [tracedPaths]);

  const handlePolygonComplete = useCallback(
    ({ coordinates }: { coordinates: Array<{ lat: number; lng: number }> }) => {
      const map = mapRef.current;
      if (!map || coordinates.length < 3) return;

      const shape = new google.maps.Polygon({
        paths: coordinates,
        map,
        fillColor: TRACE_FILL_COLOR,
        fillOpacity: 0.3,
        strokeColor: TRACE_FILL_COLOR,
        strokeWeight: 2,
        clickable: false,
      });
      tracedShapesRef.current.push(shape);
      setTracedPaths((previous) => [...previous, coordinates]);
      setTracing(false);
    },
    [],
  );

  // The next house gets a clean map: the shapes belong to the old one, and its
  // map instance is thrown away when the drawer closes.
  useEffect(() => {
    clearTraces();
    setTracing(false);
  }, [open, pin?.id, clearTraces]);

  usePolygonDrawing({
    map: mapReady ? mapRef.current : null,
    isActive: tracing,
    fillColor: TRACE_FILL_COLOR,
    snapTargets,
    onPolygonComplete: handlePolygonComplete,
  });

  // computeArea returns the footprint the roof covers, which is exactly what
  // the tier's pitch multiplier expects — it is the multiplier's job to turn
  // that into sloped surface. Correcting for slope here would charge for the
  // pitch twice.
  const tracedSqft = useMemo(() => {
    if (!tracedPaths.length || !window.google?.maps?.geometry) return null;
    const total = tracedPaths.reduce(
      (sum, path) => sum + google.maps.geometry.spherical.computeArea(path),
      0,
    );
    return total > 0 ? total * SQFT_PER_SQUARE_METRE : null;
  }, [tracedPaths]);

  // A trace overwrites the field so the number always matches the shape on the
  // map; typing afterwards still wins until the next outline is drawn.
  useEffect(() => {
    if (tracedSqft === null) return;
    setUnit('sqft');
    setArea(tracedSqft.toFixed(0));
  }, [tracedSqft]);

  const areaValue = numberOrNull(area);
  const roofAreaSqft = areaValue === null ? null : unit === 'squares' ? areaValue * SQFT_PER_SQUARE : areaValue;
  const bucket = bucketForPitch(pitch);
  const wasteValue = wastePercent.trim() ? Number(wastePercent) : null;
  const validWaste = wasteValue === null || (Number.isFinite(wasteValue) && wasteValue >= 0 && wasteValue <= 100);

  // The waste factor the server will price with: what is typed here, else the
  // one already stored on the pin (leaving the field empty does not clear it),
  // else the tier's own. Previewing the tier default over a stored override is
  // how the measurer ends up seeing a different price from the printed one.
  const pinWasteFactor = pin?.waste_factor != null ? moneyToNumber(pin.waste_factor) : null;
  const tierWasteFactor = activeTier ? moneyToNumber(activeTier.waste_factor) : 0;
  const appliedWaste =
    wasteValue !== null ? wasteValue / 100 : pinWasteFactor ?? tierWasteFactor;
  const appliedWasteSource =
    wasteValue !== null
      ? 'as entered'
      : pinWasteFactor !== null
        ? "this pin's saved factor"
        : `${activeTier ? activeTier.name : 'tier'} default`;

  const preview = useMemo(
    () =>
      previewQuote({
        tier: activeTier,
        rangePct: widget?.price_range_pct ?? '0',
        roofAreaSqft: roofAreaSqft ?? 0,
        bucket,
        wasteFactor: appliedWaste,
      }),
    [activeTier, widget?.price_range_pct, roofAreaSqft, bucket, appliedWaste],
  );

  const canSave = pin !== null && roofAreaSqft !== null && validWaste && !saving;

  const submit = async (advance: boolean) => {
    if (!pin || roofAreaSqft === null || !validWaste) return;
    setSaving(true);
    setError(null);
    try {
      const payload: CompleteMeasurementRequest = {
        roof_area_sqft: roofAreaSqft.toFixed(2),
        predominant_pitch: pitch,
        pitch_bucket: bucket,
      };
      if (wasteValue !== null) {
        payload.waste_factor = (wasteValue / 100).toFixed(3);
      }
      if (tierId !== null) {
        payload.tier_id = tierId;
      }
      const updated = await mailerPinsApi.completeMeasurement(pin.id, payload);
      setSaved(updated);
      onSaved(updated, advance);
    } catch (err) {
      setError(getApiErrorMessage(err, 'That measurement could not be saved.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      anchor="right"
      size="lg"
      // Tracing a roof by hand needs room: the map gets most of the screen so
      // corners can be clicked accurately instead of pixel-hunting in a strip.
      slotProps={{ content: { sx: { width: { xs: '100%', sm: '85%', md: '65%' } } } }}
    >
      <ModalClose />
      <Box sx={{ p: 2, pt: 5, overflowY: 'auto' }}>
        {!pin ? (
          <Typography level="body-sm">No pin selected.</Typography>
        ) : (
          <Stack spacing={2}>
            <Box>
              <Typography level="title-md">Quick measure</Typography>
              <Typography level="body-sm" sx={{ mt: 0.5, wordBreak: 'break-word' }}>
                {pin.formatted_address}
              </Typography>
              <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                {pin.campaign_name ? `Campaign: ${pin.campaign_name}` : 'No campaign assigned yet'}
              </Typography>
            </Box>

            {/* Above the map, not over it — as an overlay it covered the very
                corners of the roof the measurer is trying to click. */}
            {tracing && (
              <Sheet
                variant="solid"
                color="primary"
                sx={{ px: 1.5, py: 1, borderRadius: 'sm' }}
              >
                <Typography level="body-xs" sx={{ color: 'inherit' }}>
                  Click each corner of the roof. Click the first corner again to close it — Esc
                  cancels.
                </Typography>
              </Sheet>
            )}

            <Sheet
              variant="outlined"
              sx={{
                borderRadius: 'sm',
                overflow: 'hidden',
                height: { xs: 280, md: 480 },
                position: 'relative',
              }}
            >
              <Box ref={containerRef} sx={{ width: '100%', height: '100%' }} />
              {!mapReady && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                    Loading satellite imagery…
                  </Typography>
                </Box>
              )}
            </Sheet>

            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Button
                size="sm"
                variant={tracing ? 'solid' : 'outlined'}
                color="primary"
                startDecorator={<TraceIcon />}
                onClick={() => setTracing((active) => !active)}
                disabled={!mapReady}
              >
                {tracing
                  ? 'Cancel'
                  : tracedPaths.length
                    ? 'Trace another section'
                    : 'Trace the roof'}
              </Button>
              {tracedPaths.length > 0 && (
                <Button
                  size="sm"
                  variant="plain"
                  color="neutral"
                  startDecorator={<UndoIcon />}
                  onClick={clearTraces}
                >
                  Clear {tracedPaths.length === 1 ? 'outline' : `${tracedPaths.length} outlines`}
                </Button>
              )}
              {tracedSqft !== null && (
                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                  Traced {Math.round(tracedSqft).toLocaleString()} sq ft of footprint
                </Typography>
              )}
            </Stack>

            <Box>
              <Typography level="title-sm">Street view</Typography>
              <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 0.5 }}>
                Look at the roof from the road to judge how steep it is.
              </Typography>
              <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'hidden' }}>
                <GoogleStreetView location={pinLatLng(pin)} height={200} />
              </Sheet>
            </Box>

            {tiers.length === 0 && (
              <Alert color="warning" variant="soft">
                <Box>
                  <Typography level="title-sm">No pricing tiers yet</Typography>
                  <Typography level="body-sm">
                    Direct mail prices come from your Roof Quote Widget. Add a tier with a price per
                    square before measuring, or this pin cannot be priced.
                  </Typography>
                  <Button
                    size="sm"
                    variant="plain"
                    component={RouterLink}
                    to={ROOF_QUOTE_SETUP_PATH}
                    sx={{ mt: 0.5, px: 0 }}
                  >
                    Set up Roof Quote pricing
                  </Button>
                </Box>
              </Alert>
            )}

            {error && (
              <Alert color="danger" variant="soft">
                {error}
              </Alert>
            )}

            <form
              onSubmit={(event) => {
                event.preventDefault();
                void submit(hasNext);
              }}
            >
              {/* The map wants the full width of the drawer; the fields do not
                  read well stretched that far, so they keep a form's width. */}
              <Stack spacing={2} sx={{ maxWidth: 720 }}>
                <FormControl>
                  <FormLabel>Roof area</FormLabel>
                  <Stack direction="row" spacing={1}>
                    <Input
                      autoFocus
                      type="number"
                      value={area}
                      onChange={(event) => setArea(event.target.value)}
                      placeholder={unit === 'squares' ? 'e.g. 28' : 'e.g. 2800'}
                      slotProps={{ input: { min: 0, step: 'any' } }}
                      sx={{ flex: 1 }}
                    />
                    <ToggleButtonGroup
                      size="sm"
                      value={unit}
                      onChange={(_, value) => {
                        if (!value || value === unit) return;
                        if (areaValue !== null) {
                          const converted = value === 'sqft' ? areaValue * SQFT_PER_SQUARE : areaValue / SQFT_PER_SQUARE;
                          setArea(String(Number(converted.toFixed(value === 'sqft' ? 2 : 4))));
                        }
                        setUnit(value as AreaUnit);
                      }}
                    >
                      <Button value="squares">Squares</Button>
                      <Button value="sqft">Sq ft</Button>
                    </ToggleButtonGroup>
                  </Stack>
                  <FormHelperText>
                    {roofAreaSqft
                      ? [
                          `${Math.round(roofAreaSqft).toLocaleString()} sq ft`,
                          `${(roofAreaSqft / SQFT_PER_SQUARE).toFixed(1)} squares`,
                          // The figure the price is actually built from, so a
                          // measurer can see the waste factor doing something.
                          preview
                            ? `${preview.squaresWithWaste.toFixed(1)} with ${(appliedWaste * 100).toFixed(0)}% waste`
                            : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')
                      : 'Footprint as seen from above — the pitch below accounts for the slope.'}
                  </FormHelperText>
                </FormControl>

                <FormControl>
                  <FormLabel>Predominant pitch</FormLabel>
                  <Select
                    value={pitch}
                    onChange={(_, value) => value && setPitch(value)}
                    slotProps={{ button: { 'aria-label': 'Predominant pitch' } }}
                  >
                    {PITCH_OPTIONS.map((option) => (
                      <Option key={option} value={option}>
                        {option}
                      </Option>
                    ))}
                  </Select>
                  <FormHelperText>Prices as {PITCH_BUCKET_LABELS[bucket].toLowerCase()}.</FormHelperText>
                </FormControl>

                <FormControl error={!validWaste}>
                  <FormLabel>Waste factor</FormLabel>
                  <Input
                    type="number"
                    value={wastePercent}
                    onChange={(event) => setWastePercent(event.target.value)}
                    endDecorator="%"
                    placeholder={
                      pinWasteFactor !== null
                        ? `Saved on this pin: ${formatWaste(pinWasteFactor)}`
                        : `Tier default ${formatWaste(tierWasteFactor)}`
                    }
                    slotProps={{ input: { min: 0, max: 100, step: 0.1 } }}
                  />
                  <FormHelperText>
                    {!validWaste
                      ? 'Enter a waste percentage from 0 to 100.'
                      : pinWasteFactor !== null
                      ? `Left blank, this roof keeps the ${formatWaste(pinWasteFactor)} already saved on it.`
                      : "Leave blank to use the tier's own waste factor."}
                  </FormHelperText>
                </FormControl>

                {tiers.length > 1 && (
                  <FormControl>
                    <FormLabel>Pricing tier</FormLabel>
                    <Select
                      value={tierId}
                      onChange={(_, value) => setTierId(value)}
                      slotProps={{ button: { 'aria-label': 'Pricing tier' } }}
                    >
                      <Option value={null}>
                        {defaultTier ? `Campaign default (${defaultTier.name})` : 'Campaign default'}
                      </Option>
                      {tiers.map((tier) => (
                        <Option key={tier.id} value={tier.id}>
                          {tier.name} — {formatMoney(tier.base_price_per_square, { decimals: 0 })}/sq
                        </Option>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <Sheet variant="soft" color="primary" sx={{ p: 1.5, borderRadius: 'sm' }}>
                  {saved ? (
                    <>
                      <Typography level="body-xs">Saved price — this is what gets printed</Typography>
                      <Typography level="h3">
                        {formatQuoteRange(saved.quote_price_low, saved.quote_price_high)}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography level="body-xs">Estimated price</Typography>
                      <Typography level="h3">
                        {preview
                          ? `${formatMoney(preview.low, { decimals: 0 })} – ${formatMoney(preview.high, {
                              decimals: 0,
                            })}`
                          : '—'}
                      </Typography>
                      <Typography level="body-xs">
                        {preview
                          ? `${preview.squaresWithWaste.toFixed(1)} squares at ${formatWaste(
                              appliedWaste,
                            )} waste (${appliedWasteSource}) · confirmed when you save`
                          : 'Enter a roof area to see the price the homeowner will see.'}
                      </Typography>
                    </>
                  )}
                </Sheet>

                <Stack direction="row" spacing={1}>
                  <Button
                    type="submit"
                    loading={saving}
                    disabled={!canSave}
                    startDecorator={<MeasureIcon />}
                    sx={{ flex: 1 }}
                  >
                    {hasNext ? 'Save & next' : 'Save measurement'}
                  </Button>
                  {hasNext && (
                    <Button
                      variant="outlined"
                      color="neutral"
                      loading={saving}
                      disabled={!canSave}
                      onClick={() => void submit(false)}
                    >
                      Save & stay
                    </Button>
                  )}
                </Stack>
              </Stack>
            </form>

            <Divider />

            <Stack spacing={1} sx={{ maxWidth: 720 }}>
              <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                Complicated roof? Quick measure is a ballpark for the mailer. Draw it properly in
                Measurements when you expect to sell the job.
              </Typography>
              <Button
                variant="outlined"
                color="neutral"
                component="a"
                href={FULL_MEASUREMENT_PATH}
                target="_blank"
                rel="noopener noreferrer"
                endDecorator={<OpenIcon />}
              >
                Open the full measurement editor
              </Button>
              <Button
                variant="plain"
                color="danger"
                startDecorator={<SkipIcon />}
                onClick={() => onSkip(pin)}
              >
                Skip this address instead
              </Button>
            </Stack>
          </Stack>
        )}
      </Box>
    </Drawer>
  );
};
